import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogoMark } from "@/components/Logo";
import api, { eur } from "@/lib/api";
import SEO from "@/components/SEO";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, LogOut } from "lucide-react";

const EMPTY_LISTING = {
  title: "", city: "", region: "", postal_code: "",
  property_type: "libre", surface: 100, rooms: 4, bedrooms: 2,
  market_value: 400000, bouquet: 100000, monthly_payment: 1800, duration_years: 15,
  description: "", features: [], images: [], published: true,
};
const EMPTY_ARTICLE = { slug: "", title: "", excerpt: "", category: "Les bases", cover_image: "", content: "", reading_time: 8, published: true };

export default function Admin() {
  const { user, logout } = useAuth();
  const [listings, setListings] = useState([]);
  const [articles, setArticles] = useState([]);
  const [messages, setMessages] = useState([]);
  const [leads, setLeads] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [tab, setTab] = useState("listings");
  const [detailSub, setDetailSub] = useState(null);

  const load = async () => {
    if (!user) return;
    const [a, b, c, d, s, iq] = await Promise.all([
      api.get("/listings?limit=500"),
      api.get("/articles"),
      api.get("/admin/messages"),
      api.get("/admin/leads"),
      api.get("/admin/submissions"),
      api.get("/admin/inquiries"),
    ]);
    setListings(a.data); setArticles(b.data); setMessages(c.data); setLeads(d.data);
    setSubmissions(s.data); setInquiries(iq.data);
  };
  useEffect(() => { if (user && user.role === "admin") load(); }, [user]);

  if (user === undefined) return <div className="py-32 text-center text-navy/60">Chargement…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/login" replace />;

  return (
    <>
      <SEO title="Back-office — venteaterme.fr" />
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-5">
              <LogoMark size={56} />
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-accent-brand font-bold mb-2">Administration</div>
                <h1 className="font-serif text-3xl md:text-4xl text-navy">Bonjour {user.name}</h1>
              </div>
            </div>
            <button onClick={logout} data-testid="admin-logout" className="text-sm flex items-center gap-2 text-navy/60 hover:text-navy border border-navy/20 px-4 py-2">
              <LogOut size={14} /> Se déconnecter
            </button>
          </div>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="mb-8">
              <TabsTrigger value="listings" data-testid="tab-listings">Annonces ({listings.length})</TabsTrigger>
              <TabsTrigger value="articles" data-testid="tab-articles">Articles ({articles.length})</TabsTrigger>
              <TabsTrigger value="messages" data-testid="tab-messages">Messages ({messages.length})</TabsTrigger>
              <TabsTrigger value="leads" data-testid="tab-leads">Leads ({leads.length})</TabsTrigger>
              <TabsTrigger value="submissions" data-testid="tab-submissions">Soumissions ({submissions.filter(s => s.status === "pending").length})</TabsTrigger>
              <TabsTrigger value="inquiries" data-testid="tab-inquiries">Intérêts ({inquiries.length})</TabsTrigger>
              <TabsTrigger value="security" data-testid="tab-security">Sécurité</TabsTrigger>
            </TabsList>

            <TabsContent value="listings">
              <ListingsPanel data={listings} refresh={load} />
            </TabsContent>
            <TabsContent value="articles">
              <ArticlesPanel data={articles} refresh={load} />
            </TabsContent>
            <TabsContent value="messages">
              <div className="bg-white border border-navy/10">
                <Table>
                  <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Profil</TableHead><TableHead>Nom</TableHead><TableHead>Email</TableHead><TableHead>Ville</TableHead><TableHead>Message</TableHead></TableRow></TableHeader>
                  <TableBody>{messages.map((m) => (
                    <TableRow key={m.id}>
                      <TableCell className="text-xs">{new Date(m.created_at).toLocaleString("fr-FR")}</TableCell>
                      <TableCell>{m.profile}</TableCell><TableCell>{m.name}</TableCell><TableCell>{m.email}</TableCell><TableCell>{m.city}</TableCell>
                      <TableCell className="max-w-xs truncate">{m.message}</TableCell>
                    </TableRow>
                  ))}</TableBody>
                </Table>
                {messages.length === 0 && <div className="py-12 text-center text-navy/50 text-sm">Aucun message.</div>}
              </div>
            </TabsContent>
            <TabsContent value="leads">
              <div className="bg-white border border-navy/10">
                <Table>
                  <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Source</TableHead><TableHead>Nom</TableHead><TableHead>Email</TableHead><TableHead>Téléphone</TableHead><TableHead>Bouquet</TableHead><TableHead>Mensualité</TableHead></TableRow></TableHeader>
                  <TableBody>{leads.map((l) => (
                    <TableRow key={l.id}>
                      <TableCell className="text-xs">{new Date(l.created_at).toLocaleString("fr-FR")}</TableCell>
                      <TableCell>{l.source}</TableCell><TableCell>{l.name}</TableCell><TableCell>{l.email}</TableCell><TableCell>{l.phone}</TableCell>
                      <TableCell>{l.bouquet ? eur(l.bouquet) : "—"}</TableCell><TableCell>{l.monthly_payment ? eur(l.monthly_payment) : "—"}</TableCell>
                    </TableRow>
                  ))}</TableBody>
                </Table>
                {leads.length === 0 && <div className="py-12 text-center text-navy/50 text-sm">Aucun lead.</div>}
              </div>
            </TabsContent>
            <TabsContent value="submissions">
              <div className="bg-white border border-navy/10">
                <Table>
                  <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Profil</TableHead><TableHead>Contact</TableHead><TableHead>Ville</TableHead><TableHead>Bien</TableHead><TableHead>Notaire</TableHead><TableHead>Statut</TableHead><TableHead></TableHead></TableRow></TableHeader>
                  <TableBody>{submissions.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell className="text-xs">{new Date(s.created_at).toLocaleDateString("fr-FR")}</TableCell>
                      <TableCell>{s.profile}{s.company ? ` (${s.company})` : ""}</TableCell>
                      <TableCell className="text-xs">{s.name}<br/>{s.email}</TableCell>
                      <TableCell>{s.city}</TableCell>
                      <TableCell className="text-xs">{s.property_type} · {s.surface} m² · {eur(s.market_value)}</TableCell>
                      <TableCell className="text-xs" data-testid={`sub-notary-cell-${s.id}`}>
                        {s.has_notary === true ? (
                          <span className="inline-block bg-accent-brand/15 text-accent-brand font-bold px-2 py-0.5">Oui{s.notary_name ? ` — ${s.notary_name}` : ""}{s.notary_city ? ` (${s.notary_city})` : ""}</span>
                        ) : s.has_notary === false ? (
                          <span className="text-navy/60">Non</span>
                        ) : (
                          <span className="text-navy/40">—</span>
                        )}
                      </TableCell>
                      <TableCell><span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 ${s.status === "pending" ? "bg-accent-brand/15 text-accent-brand" : s.status === "approved" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>{s.status}</span></TableCell>
                      <TableCell className="text-right space-x-2 whitespace-nowrap">
                        <button data-testid={`detail-${s.id}`} onClick={() => setDetailSub(s)} className="text-xs border border-navy/20 px-3 py-1 hover:bg-navy/5">Détail</button>
                        {s.status === "pending" && (<>
                          <button data-testid={`approve-${s.id}`} onClick={async () => { await api.post(`/admin/submissions/${s.id}/approve`); toast.success("Annonce publiée"); load(); }} className="text-xs bg-navy text-cream px-3 py-1 hover:bg-[#111C2C]">Publier</button>
                          <button data-testid={`reject-${s.id}`} onClick={async () => { await api.post(`/admin/submissions/${s.id}/reject`); toast.success("Refusée"); load(); }} className="text-xs border border-navy/20 px-3 py-1 hover:bg-navy/5">Refuser</button>
                        </>)}
                      </TableCell>
                    </TableRow>
                  ))}</TableBody>
                </Table>
                {submissions.length === 0 && <div className="py-12 text-center text-navy/50 text-sm">Aucune soumission.</div>}
              </div>
            </TabsContent>
            <TabsContent value="inquiries">
              <div className="bg-white border border-navy/10">
                <Table>
                  <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Bien</TableHead><TableHead>Nom</TableHead><TableHead>Email</TableHead><TableHead>Téléphone</TableHead><TableHead>Message</TableHead></TableRow></TableHeader>
                  <TableBody>{inquiries.map((i) => (
                    <TableRow key={i.id}>
                      <TableCell className="text-xs">{new Date(i.created_at).toLocaleDateString("fr-FR")}</TableCell>
                      <TableCell className="text-xs max-w-xs truncate">{i.listing_title}</TableCell>
                      <TableCell>{i.name}</TableCell><TableCell>{i.email}</TableCell><TableCell>{i.phone}</TableCell>
                      <TableCell className="max-w-xs truncate text-xs">{i.message}</TableCell>
                    </TableRow>
                  ))}</TableBody>
                </Table>
                {inquiries.length === 0 && <div className="py-12 text-center text-navy/50 text-sm">Aucun intérêt.</div>}
              </div>
            </TabsContent>
            <TabsContent value="security">
              <SecurityPanel user={user} />
            </TabsContent>
          </Tabs>
        </div>
      </section>

      <Dialog open={!!detailSub} onOpenChange={(o) => !o && setDetailSub(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="submission-detail">
          <DialogHeader><DialogTitle>Détail de la soumission</DialogTitle></DialogHeader>
          {detailSub && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><div className="text-[10px] uppercase text-navy/50 font-bold">Date</div><div>{new Date(detailSub.created_at).toLocaleString("fr-FR")}</div></div>
                <div><div className="text-[10px] uppercase text-navy/50 font-bold">Statut</div><div className="capitalize">{detailSub.status}</div></div>
                <div><div className="text-[10px] uppercase text-navy/50 font-bold">Profil</div><div className="capitalize">{detailSub.profile}{detailSub.company ? ` — ${detailSub.company}` : ""}{detailSub.siret ? ` (SIRET ${detailSub.siret})` : ""}</div></div>
                <div><div className="text-[10px] uppercase text-navy/50 font-bold">Contact</div><div>{detailSub.name}<br/>{detailSub.email}<br/>{detailSub.phone}</div></div>
              </div>

              <div className="border-t pt-3 grid grid-cols-2 gap-3">
                <div><div className="text-[10px] uppercase text-navy/50 font-bold">Ville / Région</div><div>{detailSub.city} · {detailSub.region}</div></div>
                <div><div className="text-[10px] uppercase text-navy/50 font-bold">Type</div><div className="capitalize">{detailSub.property_type}</div></div>
                <div><div className="text-[10px] uppercase text-navy/50 font-bold">Surface / Pièces</div><div>{detailSub.surface} m² · {detailSub.rooms} pièces</div></div>
                <div><div className="text-[10px] uppercase text-navy/50 font-bold">Prix / Bouquet</div><div>{eur(detailSub.market_value)} — bouquet {eur(detailSub.bouquet)}</div></div>
                <div><div className="text-[10px] uppercase text-navy/50 font-bold">Durée</div><div>{detailSub.duration_years} ans</div></div>
                <div><div className="text-[10px] uppercase text-navy/50 font-bold">DPE</div><div>Énergie {detailSub.dpe_energy || "—"} / Climat {detailSub.dpe_climate || "—"}</div></div>
              </div>

              <div className="border-t pt-3">
                <div className="text-[10px] uppercase text-accent-brand font-bold mb-2">Notaire du vendeur</div>
                {detailSub.has_notary === true && (
                  <div data-testid="detail-notary-yes" className="bg-accent-brand/5 border-l-4 border-accent-brand p-3">
                    <div className="font-bold text-navy">Oui — le vendeur a déjà un notaire</div>
                    <div className="mt-1 text-xs text-navy/70">
                      <div>Nom / étude&nbsp;: <strong>{detailSub.notary_name || "(non renseigné)"}</strong></div>
                      <div>Ville&nbsp;: <strong>{detailSub.notary_city || "(non renseignée)"}</strong></div>
                    </div>
                  </div>
                )}
                {detailSub.has_notary === false && (
                  <div data-testid="detail-notary-no" className="bg-navy/5 border-l-4 border-navy/30 p-3 text-navy/70">
                    Non&nbsp;— le vendeur n'a pas encore de notaire attitré.
                    {detailSub.wants_partner_notary === true && (
                      <div className="mt-2 pt-2 border-t border-navy/15" data-testid="detail-wants-partner">
                        <div className="text-[10px] uppercase tracking-widest text-accent-brand font-bold mb-1">Mise en relation demandée</div>
                        <div className="text-navy">
                          {detailSub.partner_notary_name ? (
                            <>Étude sélectionnée&nbsp;: <strong>{detailSub.partner_notary_name}</strong></>
                          ) : (
                            <>À orienter manuellement vers un notaire du département <strong>{(detailSub.postal_code || "").slice(0, 2) || "?"}</strong>.</>
                          )}
                        </div>
                        <div className="text-xs text-navy/60 mt-1">Code postal du bien&nbsp;: {detailSub.postal_code || "—"} · Contact&nbsp;: {detailSub.email} — {detailSub.phone || "n/c"}</div>
                      </div>
                    )}
                    {detailSub.wants_partner_notary === false && (
                      <div className="mt-2 text-[11px] italic text-navy/50">Le vendeur ne souhaite pas être mis en relation.</div>
                    )}
                  </div>
                )}
                {detailSub.has_notary === null || detailSub.has_notary === undefined ? (
                  <div className="text-xs text-navy/50 italic">Question non renseignée (soumission antérieure au champ notaire).</div>
                ) : null}
              </div>

              {detailSub.description && (
                <div className="border-t pt-3">
                  <div className="text-[10px] uppercase text-navy/50 font-bold mb-1">Description</div>
                  <p className="text-navy/80 whitespace-pre-line">{detailSub.description}</p>
                </div>
              )}

              {detailSub.images && detailSub.images.length > 0 && (
                <div className="border-t pt-3">
                  <div className="text-[10px] uppercase text-navy/50 font-bold mb-2">Photos ({detailSub.images.length})</div>
                  <div className="grid grid-cols-4 gap-2">
                    {detailSub.images.map((u, i) => <img key={i} src={u} alt="" className="aspect-[4/3] object-cover" />)}
                  </div>
                </div>
              )}

              <div className="border-t pt-3">
                <div className="text-[10px] uppercase text-accent-brand font-bold mb-2">Vérification de propriété</div>
                <div className="flex items-center gap-3 mb-3">
                  {detailSub.is_verified ? (
                    <span data-testid="detail-is-verified" className="inline-flex items-center gap-2 bg-accent-brand text-white text-[10px] uppercase tracking-widest font-bold px-3 py-1.5">
                      <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 10-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                      Bien vérifié
                    </span>
                  ) : (
                    <span className="inline-block bg-navy/10 text-navy text-[10px] uppercase tracking-widest font-bold px-3 py-1.5">Non vérifié</span>
                  )}
                  <button
                    type="button"
                    data-testid={`toggle-verify-${detailSub.id}`}
                    onClick={async () => {
                      await api.post(`/admin/submissions/${detailSub.id}/verify`, { is_verified: !detailSub.is_verified });
                      toast.success(!detailSub.is_verified ? "Bien marqué comme vérifié" : "Badge retiré");
                      const fresh = { ...detailSub, is_verified: !detailSub.is_verified };
                      setDetailSub(fresh);
                      load();
                    }}
                    className="text-xs border border-navy/20 px-3 py-1 hover:bg-navy/5"
                  >
                    {detailSub.is_verified ? "Retirer le badge" : "Marquer comme vérifié"}
                  </button>
                </div>
                <div className="text-xs text-navy/60 mb-2">
                  Justificatifs transmis&nbsp;: <strong>{(detailSub.proof_file_ids || []).length}</strong>
                </div>
                {(detailSub.proof_file_ids || []).length > 0 ? (
                  <ul className="text-xs space-y-1">
                    {(detailSub.proof_file_ids || []).map((fid) => (
                      <li key={fid}>
                        <a
                          href={`${api.defaults.baseURL}/admin/proofs/${fid}`}
                          target="_blank" rel="noopener noreferrer"
                          data-testid={`proof-link-${fid}`}
                          className="text-accent-brand underline font-mono break-all"
                        >{fid}</a>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-xs italic text-navy/50">Aucun justificatif transmis pour l'instant. Demander à l'auteur par email si nécessaire.</div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function ListingsPanel({ data, refresh }) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);

  const del = async (id) => {
    if (!window.confirm("Supprimer cette annonce ?")) return;
    try { await api.delete(`/admin/listings/${id}`); toast.success("Annonce supprimée"); refresh(); }
    catch { toast.error("Erreur"); }
  };

  return (
    <>
      <div className="flex justify-end mb-4">
        <button data-testid="new-listing-btn" onClick={() => { setCurrent(null); setOpen(true); }}
          className="bg-navy text-cream px-4 py-2 text-sm font-bold tracking-wide flex items-center gap-2 hover:bg-[#111C2C]">
          <Plus size={16} /> Nouvelle annonce
        </button>
      </div>
      <div className="bg-white border border-navy/10">
        <Table>
          <TableHeader><TableRow><TableHead>Titre</TableHead><TableHead>Ville</TableHead><TableHead>Type</TableHead><TableHead>Bouquet</TableHead><TableHead>Mensualité</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>{data.map((l) => (
            <TableRow key={l.id}>
              <TableCell className="max-w-xs truncate">{l.title}</TableCell>
              <TableCell>{l.city}</TableCell><TableCell>{l.property_type}</TableCell>
              <TableCell>{eur(l.bouquet)}</TableCell><TableCell>{eur(l.monthly_payment)}</TableCell>
              <TableCell className="text-right space-x-2">
                <button onClick={() => { setCurrent(l); setOpen(true); }} className="p-1 text-navy/60 hover:text-accent-brand"><Pencil size={16} /></button>
                <button data-testid={`del-listing-${l.id}`} onClick={() => del(l.id)} className="p-1 text-navy/60 hover:text-destructive"><Trash2 size={16} /></button>
              </TableCell>
            </TableRow>
          ))}</TableBody>
        </Table>
      </div>
      <ListingDialog open={open} setOpen={setOpen} current={current} refresh={refresh} />
    </>
  );
}

function ListingDialog({ open, setOpen, current, refresh }) {
  const [f, setF] = useState(EMPTY_LISTING);
  useEffect(() => { setF(current ? { ...current, features: current.features || [], images: current.images || [] } : EMPTY_LISTING); }, [current, open]);

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...f, features: typeof f.features === "string" ? f.features.split(",").map((s) => s.trim()).filter(Boolean) : f.features,
      images: typeof f.images === "string" ? f.images.split(",").map((s) => s.trim()).filter(Boolean) : f.images };
    try {
      if (current?.id) await api.put(`/admin/listings/${current.id}`, payload);
      else await api.post("/admin/listings", payload);
      toast.success("Enregistré"); setOpen(false); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{current ? "Modifier l'annonce" : "Nouvelle annonce"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid grid-cols-2 gap-3 pt-2" data-testid="listing-form">
          <div className="col-span-2"><Label>Titre</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} required /></div>
          <div><Label>Ville</Label><Input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} required /></div>
          <div><Label>Région</Label><Input value={f.region} onChange={(e) => setF({ ...f, region: e.target.value })} /></div>
          <div><Label>Code postal</Label><Input value={f.postal_code} onChange={(e) => setF({ ...f, postal_code: e.target.value })} /></div>
          <div><Label>Type</Label>
            <Select value={f.property_type} onValueChange={(v) => setF({ ...f, property_type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="libre">Libre</SelectItem><SelectItem value="occupee">Occupée</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label>Surface (m²)</Label><Input type="number" value={f.surface} onChange={(e) => setF({ ...f, surface: +e.target.value })} /></div>
          <div><Label>Pièces</Label><Input type="number" value={f.rooms} onChange={(e) => setF({ ...f, rooms: +e.target.value })} /></div>
          <div><Label>Chambres</Label><Input type="number" value={f.bedrooms} onChange={(e) => setF({ ...f, bedrooms: +e.target.value })} /></div>
          <div><Label>Valeur (€)</Label><Input type="number" value={f.market_value} onChange={(e) => setF({ ...f, market_value: +e.target.value })} /></div>
          <div><Label>Bouquet (€)</Label><Input type="number" value={f.bouquet} onChange={(e) => setF({ ...f, bouquet: +e.target.value })} /></div>
          <div><Label>Mensualité (€)</Label><Input type="number" value={f.monthly_payment} onChange={(e) => setF({ ...f, monthly_payment: +e.target.value })} /></div>
          <div><Label>Durée (années)</Label><Input type="number" value={f.duration_years} onChange={(e) => setF({ ...f, duration_years: +e.target.value })} /></div>
          <div className="col-span-2"><Label>Caractéristiques (séparées par des virgules)</Label>
            <Input value={Array.isArray(f.features) ? f.features.join(", ") : f.features} onChange={(e) => setF({ ...f, features: e.target.value })} /></div>
          <div className="col-span-2"><Label>Images (URLs séparées par des virgules)</Label>
            <Input value={Array.isArray(f.images) ? f.images.join(", ") : f.images} onChange={(e) => setF({ ...f, images: e.target.value })} /></div>
          <div className="col-span-2"><Label>Description</Label>
            <Textarea rows={4} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
          <DialogFooter className="col-span-2 mt-2">
            <button type="submit" data-testid="listing-save" className="bg-accent-brand text-white px-6 py-2 font-bold">Enregistrer</button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ArticlesPanel({ data, refresh }) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const del = async (id) => {
    if (!window.confirm("Supprimer cet article ?")) return;
    try { await api.delete(`/admin/articles/${id}`); toast.success("Supprimé"); refresh(); }
    catch { toast.error("Erreur"); }
  };
  return (
    <>
      <div className="flex justify-end mb-4">
        <button data-testid="new-article-btn" onClick={() => { setCurrent(null); setOpen(true); }} className="bg-navy text-cream px-4 py-2 text-sm font-bold flex items-center gap-2 hover:bg-[#111C2C]">
          <Plus size={16} /> Nouvel article
        </button>
      </div>
      <div className="bg-white border border-navy/10">
        <Table>
          <TableHeader><TableRow><TableHead>Titre</TableHead><TableHead>Catégorie</TableHead><TableHead>Slug</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>{data.map((a) => (
            <TableRow key={a.id}>
              <TableCell className="max-w-xs truncate">{a.title}</TableCell><TableCell>{a.category}</TableCell><TableCell className="text-xs">{a.slug}</TableCell>
              <TableCell className="text-right space-x-2">
                <button onClick={() => { setCurrent(a); setOpen(true); }} className="p-1"><Pencil size={16} /></button>
                <button onClick={() => del(a.id)} className="p-1 hover:text-destructive"><Trash2 size={16} /></button>
              </TableCell>
            </TableRow>
          ))}</TableBody>
        </Table>
      </div>
      <ArticleDialog open={open} setOpen={setOpen} current={current} refresh={refresh} />
    </>
  );
}

function ArticleDialog({ open, setOpen, current, refresh }) {
  const [f, setF] = useState(EMPTY_ARTICLE);
  useEffect(() => { setF(current || EMPTY_ARTICLE); }, [current, open]);
  const save = async (e) => {
    e.preventDefault();
    try {
      if (current?.id) await api.put(`/admin/articles/${current.id}`, f);
      else await api.post("/admin/articles", f);
      toast.success("Enregistré"); setOpen(false); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{current ? "Modifier l'article" : "Nouvel article"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3" data-testid="article-form">
          <div><Label>Titre</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} required /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Slug</Label><Input value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} required /></div>
            <div><Label>Catégorie</Label>
              <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Les bases">Les bases</SelectItem>
                  <SelectItem value="Comparatif">Comparatif</SelectItem>
                  <SelectItem value="Fiscalité">Fiscalité</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Extrait</Label><Textarea rows={2} value={f.excerpt} onChange={(e) => setF({ ...f, excerpt: e.target.value })} /></div>
          <div><Label>Image (URL)</Label><Input value={f.cover_image} onChange={(e) => setF({ ...f, cover_image: e.target.value })} /></div>
          <div><Label>Contenu (Markdown)</Label><Textarea rows={12} value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} /></div>
          <div><Label>Temps de lecture (min)</Label><Input type="number" value={f.reading_time} onChange={(e) => setF({ ...f, reading_time: +e.target.value })} /></div>
          <DialogFooter><button type="submit" data-testid="article-save" className="bg-accent-brand text-white px-6 py-2 font-bold">Enregistrer</button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function SecurityPanel({ user }) {
  const [enabled, setEnabled] = useState(!!user?.totp_enabled);
  const [setup, setSetup] = useState(null); // {secret, qr_png_base64, otpauth_uri}
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState(null);
  const [disableCode, setDisableCode] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Refresh live status from server
    api.get("/auth/me").then(({ data }) => setEnabled(!!data.totp_enabled)).catch(() => {});
  }, []);

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetup(data);
    } catch { toast.error("Erreur setup 2FA"); }
    finally { setBusy(false); }
  };

  const enable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/enable", { code });
      setBackupCodes(data.backup_codes);
      setEnabled(true);
      setSetup(null); setCode("");
      toast.success("2FA activée. Sauvegardez vos codes de secours.");
    } catch (err) { toast.error(err.response?.data?.detail || "Code invalide"); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    if (!window.confirm("Désactiver la 2FA ? Le compte reviendra à une simple protection par mot de passe.")) return;
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { code: disableCode });
      setEnabled(false); setDisableCode(""); setBackupCodes(null);
      toast.success("2FA désactivée.");
    } catch (err) { toast.error(err.response?.data?.detail || "Code invalide"); }
    finally { setBusy(false); }
  };

  const regen = async () => {
    if (!window.confirm("Régénérer les codes de secours ? Les anciens ne fonctionneront plus.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/regenerate-backup", { code: disableCode });
      setBackupCodes(data.backup_codes);
      setDisableCode("");
      toast.success("Nouveaux codes de secours générés.");
    } catch (err) { toast.error(err.response?.data?.detail || "Code invalide"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-navy/10 p-8 max-w-2xl">
      <div className="text-xs uppercase tracking-widest text-accent-brand font-bold mb-2">Sécurité du compte</div>
      <h2 className="font-serif text-2xl text-navy mb-6">Vérification en 2 étapes (TOTP)</h2>

      {!enabled && !setup && (
        <div>
          <p className="text-sm text-navy/70 mb-4">
            Ajoutez une couche de sécurité à votre back-office : un code à 6 chiffres généré par
            Google Authenticator, 1Password, Authy… sera demandé à chaque connexion.
          </p>
          <button data-testid="2fa-start-setup" onClick={startSetup} disabled={busy}
            className="bg-accent-brand text-white px-6 py-3 font-bold tracking-wide hover:bg-[#B08D22] disabled:opacity-60">
            Activer la 2FA
          </button>
        </div>
      )}

      {setup && (
        <form onSubmit={enable} data-testid="2fa-setup-form" className="space-y-4">
          <p className="text-sm text-navy/70">
            1. Scannez ce QR code dans votre application d'authentification.
          </p>
          <img src={setup.qr_png_base64} alt="QR code TOTP" className="w-56 h-56 border border-navy/10" />
          <p className="text-xs text-navy/60">Ou saisissez manuellement ce secret : <code className="bg-navy/5 px-2 py-1 font-mono text-navy">{setup.secret}</code></p>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-navy/60 font-bold">2. Entrez le code affiché par votre application</Label>
            <Input data-testid="2fa-verify-code" required autoFocus value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" className="mt-2 border-navy/20 tracking-widest" />
          </div>
          <button type="submit" disabled={busy} data-testid="2fa-enable-btn" className="bg-accent-brand text-white px-6 py-3 font-bold tracking-wide hover:bg-[#B08D22] disabled:opacity-60">
            Vérifier et activer
          </button>
        </form>
      )}

      {enabled && (
        <div className="space-y-6">
          <div className="p-3 border-l-4 border-accent-brand bg-accent-brand/5 text-sm text-navy">
            <strong>2FA activée.</strong> Un code à 6 chiffres est demandé à chaque connexion.
          </div>

          {backupCodes && (
            <div className="border border-navy/20 p-6" data-testid="2fa-backup-codes">
              <div className="text-xs uppercase tracking-widest text-accent-brand font-bold mb-2">Codes de secours (à sauvegarder)</div>
              <p className="text-xs text-navy/60 mb-4">Utilisables une seule fois chacun, en cas de perte d'accès à votre application d'authentification. Copiez-les et rangez-les dans un endroit sûr — ils ne seront plus affichés.</p>
              <div className="grid grid-cols-2 gap-2 font-mono text-sm text-navy">
                {backupCodes.map((c, i) => <div key={i} className="bg-navy/5 px-3 py-2">{c}</div>)}
              </div>
            </div>
          )}

          <div className="border-t border-navy/10 pt-6 space-y-3">
            <div className="text-sm font-bold text-navy">Gérer la 2FA</div>
            <Label className="text-[10px] uppercase tracking-wider text-navy/60 font-bold">Code actuel à 6 chiffres</Label>
            <Input value={disableCode} onChange={(e) => setDisableCode(e.target.value)} placeholder="123456" className="border-navy/20 tracking-widest max-w-xs" />
            <div className="flex flex-wrap gap-3">
              <button onClick={regen} disabled={busy} data-testid="2fa-regen-btn" className="border border-navy/20 px-4 py-2 text-sm font-bold text-navy hover:bg-navy/5">
                Régénérer les codes de secours
              </button>
              <button onClick={disable} disabled={busy} data-testid="2fa-disable-btn" className="border border-red-300 text-red-700 px-4 py-2 text-sm font-bold hover:bg-red-50">
                Désactiver la 2FA
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
